'use client';

import * as React from 'react';
import { cva } from 'class-variance-authority';

import { cn } from '@utils';
import type { HeroMediaStoryblok } from '@utils/storyblok/storyblok';
import { Image } from '@common/layout/Image';
import { Button } from '@common/storyblok/Button';

type HeroVideoSource =
  | {
      src: string;
      type: 'file';
    }
  | {
      src: string;
      type: 'youtube';
    };

const MOBILE_VIDEO_DELAY_MS = 1200;
const MOBILE_VIDEO_IDLE_TIMEOUT_MS = 2500;

type IdleWindow = typeof window & {
  cancelIdleCallback?: (handle: number) => void;
  requestIdleCallback?: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number;
};

function getYouTubeVideoId(url: string) {
  try {
    const parsedUrl = new URL(url);
    const hostname = parsedUrl.hostname.replace(/^www\./, '');
    const pathParts = parsedUrl.pathname.split('/').filter(Boolean);

    if (hostname === 'youtu.be') {
      return pathParts[0] ?? null;
    }

    if (!hostname.endsWith('youtube.com') && !hostname.endsWith('youtube-nocookie.com')) {
      return null;
    }

    if (pathParts[0] === 'embed' || pathParts[0] === 'shorts' || pathParts[0] === 'live') {
      return pathParts[1] ?? null;
    }

    return parsedUrl.searchParams.get('v');
  } catch {
    return null;
  }
}

function getHeroVideoSource(src: string): HeroVideoSource {
  const youtubeId = getYouTubeVideoId(src);

  if (!youtubeId) {
    return {
      src,
      type: 'file',
    };
  }

  const params = new URLSearchParams({
    autoplay: '1',
    controls: '0',
    disablekb: '1',
    fs: '0',
    iv_load_policy: '3',
    loop: '1',
    modestbranding: '1',
    mute: '1',
    playlist: youtubeId,
    playsinline: '1',
    rel: '0',
  });

  return {
    src: `https://www.youtube-nocookie.com/embed/${youtubeId}?${params.toString()}`,
    type: 'youtube',
  };
}

const heroMediaVariants = cva('w-full relative overflow-hidden', {
  variants: {
    height: {
      full: 'min-h-screen',
      large: 'min-h-[78vh]',
      medium: 'min-h-[60vh]',
    },
  },
  defaultVariants: {
    height: 'full',
  },
});

export const HeroMedia = React.forwardRef<HTMLElement, HeroMediaStoryblok>(
  (
    {
      mediaType = 'image',
      videoUrl: _videoUrl,
      posterImage,
      image,
      eyebrow,
      title,
      text,
      ctas,
      overlayStrength = 'none',
      height = 'full',
      video,
      primaryCtaLink: _primaryCtaLink,
      primaryCtaLabel: _primaryCtaLabel,
      secondaryCtaLink: _secondaryCtaLink,
      secondaryCtaLabel: _secondaryCtaLabel,
      ...props
    },
    ref,
  ) => {
    const [playerWidth, setPlayerWidth] = React.useState('100vw');
    const [playerHeight, setPlayerHeight] = React.useState('100vh');
    const [shouldRenderVideo, setShouldRenderVideo] = React.useState(false);
    const heroVideoSource = React.useMemo(() => {
      if (mediaType !== 'video' || !video?.filename) {
        return null;
      }

      return getHeroVideoSource(video.filename);
    }, [mediaType, video?.filename]);

    React.useEffect(() => {
      const updatePlayerSize = () => {
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const aspectRatio = 16 / 9;

        if (viewportWidth / viewportHeight < aspectRatio) {
          setPlayerWidth(`${viewportHeight * aspectRatio}px`);
          setPlayerHeight(`${viewportHeight}px`);
          return;
        }

        setPlayerWidth(`${viewportWidth}px`);
        setPlayerHeight(`${viewportWidth / aspectRatio}px`);
      };

      updatePlayerSize();
      window.addEventListener('resize', updatePlayerSize);
      return () => window.removeEventListener('resize', updatePlayerSize);
    }, []);

    React.useEffect(() => {
      if (!heroVideoSource) {
        setShouldRenderVideo(false);
        return;
      }

      const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      const desktopQuery = window.matchMedia('(min-width: 768px)');
      const idleWindow = window as IdleWindow;
      let delayTimeoutId: number | null = null;
      let idleCallbackId: number | null = null;
      let isEffectActive = true;
      let isWaitingForPageLoad = false;

      const renderVideo = () => {
        delayTimeoutId = null;
        idleCallbackId = null;

        if (!isEffectActive || reducedMotionQuery.matches) {
          return;
        }

        setShouldRenderVideo(true);
      };

      const scheduleMobileVideo = () => {
        delayTimeoutId = window.setTimeout(() => {
          delayTimeoutId = null;

          if (idleWindow.requestIdleCallback) {
            idleCallbackId = idleWindow.requestIdleCallback(renderVideo, {
              timeout: MOBILE_VIDEO_IDLE_TIMEOUT_MS,
            });
            return;
          }

          delayTimeoutId = window.setTimeout(renderVideo, 1);
        }, MOBILE_VIDEO_DELAY_MS);
      };

      const handlePageLoad = () => {
        isWaitingForPageLoad = false;
        scheduleMobileVideo();
      };

      const cancelScheduledVideo = () => {
        if (isWaitingForPageLoad) {
          window.removeEventListener('load', handlePageLoad);
          isWaitingForPageLoad = false;
        }

        if (delayTimeoutId !== null) {
          window.clearTimeout(delayTimeoutId);
          delayTimeoutId = null;
        }

        if (idleCallbackId !== null && idleWindow.cancelIdleCallback) {
          idleWindow.cancelIdleCallback(idleCallbackId);
          idleCallbackId = null;
        }
      };

      const syncVideoRender = () => {
        cancelScheduledVideo();

        if (reducedMotionQuery.matches) {
          setShouldRenderVideo(false);
          return;
        }

        if (desktopQuery.matches) {
          setShouldRenderVideo(true);
          return;
        }

        setShouldRenderVideo(false);

        if (document.readyState === 'complete') {
          scheduleMobileVideo();
          return;
        }

        isWaitingForPageLoad = true;
        window.addEventListener('load', handlePageLoad, { once: true });
      };

      syncVideoRender();
      reducedMotionQuery.addEventListener('change', syncVideoRender);
      desktopQuery.addEventListener('change', syncVideoRender);

      return () => {
        isEffectActive = false;
        cancelScheduledVideo();
        reducedMotionQuery.removeEventListener('change', syncVideoRender);
        desktopQuery.removeEventListener('change', syncVideoRender);
      };
    }, [heroVideoSource]);

    const getOverlayClass = () => {
      switch (overlayStrength) {
        case 'light':
          return 'bg-black/20';
        case 'medium':
          return 'bg-black/40';
        case 'dark':
          return 'bg-black/60';
        default:
          return '';
      }
    };

    return (
      <section
        data-site-hero-media="true"
        ref={ref}
        className={cn(heroMediaVariants({ height }), 'flex items-end')}
        {...props}
      >
        {posterImage?.filename && (
          <div className="absolute inset-0">
            <Image
              asset={{
                ...posterImage,
                alt: posterImage.alt || title || 'Poster afbeelding',
              }}
              className="object-cover"
              imageSizes="100vw"
              priority
            />
          </div>
        )}

        {heroVideoSource && shouldRenderVideo ? (
          <div className="absolute inset-0 overflow-hidden">
            {heroVideoSource.type === 'youtube' ? (
              <iframe
                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                aria-hidden="true"
                className="border-0"
                height={playerHeight}
                referrerPolicy="strict-origin-when-cross-origin"
                src={heroVideoSource.src}
                style={{
                  height: playerHeight,
                  left: '50%',
                  pointerEvents: 'none',
                  position: 'absolute',
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: playerWidth,
                }}
                tabIndex={-1}
                title={title ? `${title} achtergrondvideo` : 'Achtergrondvideo'}
                width={playerWidth}
              />
            ) : (
              <video
                aria-hidden="true"
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
                src={heroVideoSource.src}
                style={{
                  height: playerHeight,
                  left: '50%',
                  pointerEvents: 'none',
                  position: 'absolute',
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: playerWidth,
                }}
                tabIndex={-1}
              />
            )}
          </div>
        ) : image?.filename ? (
          <div className="absolute inset-0">
            <Image
              asset={{
                ...image,
                alt: image.alt || title || 'Hero image',
              }}
              className="object-cover"
              imageSizes="100vw"
              priority
            />
          </div>
        ) : null}

        {overlayStrength !== 'none' && (
          <div className={cn('absolute inset-0 z-10', getOverlayClass())} />
        )}

        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/20 to-transparent" />

        {(eyebrow || title || text || (ctas && ctas.length > 0)) && (
          <div className="relative z-20 w-full pb-12 pt-40 md:pb-18 md:pt-48">
            <div className="container">
              <div className="max-w-3xl text-white">
                {eyebrow && (
                  <p className="text-sm uppercase tracking-[0.24em] text-white/78">{eyebrow}</p>
                )}

                {title && (
                  <h1 className="mt-4 font-heading text-5xl leading-none sm:text-6xl lg:text-7xl">
                    {title}
                  </h1>
                )}

                {text && (
                  <p className="mt-5 max-w-2xl text-base leading-8 text-white/82 md:text-lg">
                    {text}
                  </p>
                )}

                {ctas && ctas.length > 0 && (
                  <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                    {ctas.map((blok) => (
                      <Button
                        {...blok}
                        key={blok._uid}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </section>
    );
  },
);

HeroMedia.displayName = 'HeroMedia';
